import { ReactElement, useState, useEffect, useRef } from 'react';
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/pagination';
import useSwiperRef from '~/ui/components/common/Swiper/useSwiperRef';

interface SwiperProps extends SwiperOptions {
  slides: any[];
  renderSlide?: (slide: any, index?: number) => ReactElement;
  className?: string;
  slideClassName?: (slide: any) => string;
  navigation?: boolean;
  pagination?: boolean;
  onSlideChange?: (swiper: any) => void;
}

const Swiper = ({
  slides,
  renderSlide,
  className,
  slideClassName,
  navigation,
  pagination,
  onSlideChange,
  ...swiperProps
}: SwiperProps): ReactElement => {
  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  useEffect(() => {
    if (swiperInstance && paginationRef.current && pagination) {
      swiperInstance.params.pagination.el = paginationRef.current;

      if (swiperInstance.pagination) {
        swiperInstance.pagination.destroy();
        swiperInstance.pagination.init();
        swiperInstance.pagination.update();
      }
    }
  }, [swiperInstance, pagination]);

  return (
    <>
      <ReactSwiper
        modules={[Navigation, Pagination, A11y]}
        navigation={
          navigation
            ? {
                nextEl,
                prevEl,
              }
            : undefined
        }
        pagination={
          pagination
            ? {
                el: paginationRef.current,
                type: 'bullets',
                clickable: true,
                bulletClass: 'swiper-pagination-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active',
              }
            : undefined
        }
        onSlideChange={onSlideChange}
        {...swiperProps}
        onSwiper={setSwiperInstance}
        className={className}
        touchEventsTarget="container"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id} className={slideClassName ? slideClassName(slide) : ''}>
            {renderSlide(slide, index)}
          </SwiperSlide>
        ))}
      </ReactSwiper>
      {navigation && (
        <div className="swiper-actions swiper-buttons">
          <div
            className="swiper-button swiper-button-prev"
            ref={prevElRef}
            role="button"
            tabIndex={0}
            aria-label="Previous button"
          />
          {pagination && <div className="carousel-pages swiper-pagination" ref={paginationRef} />}
          <div
            className="swiper-button swiper-button-next"
            ref={nextElRef}
            role="button"
            tabIndex={0}
            aria-label="Next button"
          />
        </div>
      )}
    </>
  );
};

export default Swiper;
